<script setup lang="ts">
import { AppSearchField, AppTypography } from '@/components';
import MarketplaceProductList from '@/components/browse/MarketplaceProductList.vue';
import StoreProductFiltersDrawer from '@/components/browse/StoreProductFiltersDrawer.vue';
import BrowseHeader from '@/components/browse/header/BrowseHeader.vue';
import SubCategories from '@/components/browse/SubCategories.vue';
import useMarketplaceProducts, {
  type MarketplaceProductFilters,
} from '@/composables/useMarketplaceProducts';
import {
  STORE_PRODUCT_SORT_OPTIONS,
  type StoreProductFilters,
  type StoreProductSort,
  type StoreStockFilter,
} from '@/composables/useStoreProducts';
import type { Category } from '@/types/Product';

definePageMeta({
  layout: 'browse',
  auth: false,
});

interface CategoryResponse {
  data: Category;
}

const router = useRouter();
const route = useRoute();
const { isMobile } = useScreenSize();
const categoryId = Number(route.params.id);
const categoryNotFound = ref(!Number.isInteger(categoryId) || categoryId < 1);

const readQueryString = (value: unknown) => (Array.isArray(value) ? value[0] : value);
const parsePositiveNumber = (value: unknown) => {
  const parsed = Number(readQueryString(value));
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
};
const parseNonNegativeNumber = (value: unknown) => {
  const parsed = Number(readQueryString(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};
const parsePriceRange = (): [number, number] | null => {
  const min = parseNonNegativeNumber(route.query.min_price);
  const max = parseNonNegativeNumber(route.query.max_price);
  return min !== null && max !== null && min <= max ? [min, max] : null;
};
const parseStock = (value: unknown): StoreStockFilter | null => {
  const stock = readQueryString(value);
  return stock === 'in_stock' || stock === 'out_of_stock' ? stock : null;
};
const parseSort = (value: unknown): StoreProductSort => {
  const sort = readQueryString(value);
  return STORE_PRODUCT_SORT_OPTIONS.some((option) => option.value === sort)
    ? (sort as StoreProductSort)
    : '';
};

const { data, execute, error, pending: categoryPending } =
  useGetData<CategoryResponse>();

if (!categoryNotFound.value) {
  await execute(`/categories/${categoryId}`);
  categoryNotFound.value = Boolean(error.value || !data.value?.data);
}

if (!categoryNotFound.value && data.value) {
  const config = useRuntimeConfig();
  const title = data.value.data.label;
  const image = config.public.baseURL + '/logo.png';

  useSeoMeta({
    title,
    description: title,
    ogTitle: title,
    ogDescription: title,
    ogImage: image,
    twitterTitle: title,
    twitterDescription: title,
    twitterImage: image,
    twitterCard: 'summary',
  });

  useSchemaOrg([defineProduct({ name: title })]);
}

const searchInput = ref(String(readQueryString(route.query.search) ?? ''));
const selectedSub = ref<number | null>(parsePositiveNumber(route.query.subcategory));
const selectedPriceRange = ref<[number, number] | null>(parsePriceRange());
const selectedStock = ref<StoreStockFilter | null>(parseStock(route.query.stock));
const selectedSort = ref<StoreProductSort>(parseSort(route.query.sort));
const currentPage = ref(parsePositiveNumber(route.query.page) ?? 1);
const isFilterDrawerOpen = ref(false);
const catalogInitialized = ref(false);

const {
  pending: productsPending,
  products,
  meta,
  filterCategories,
  priceRange,
  stockCounts,
  facetTotal,
  fetchProducts,
  fetchFilters,
} = useMarketplaceProducts();

const activeFilters = (): MarketplaceProductFilters => ({
  categoryId,
  subcategoryId: selectedSub.value,
  priceRange: selectedPriceRange.value,
  stock: selectedStock.value,
});

const drawerFilters = computed<StoreProductFilters>(() => ({
  categoryId,
  subcategoryId: selectedSub.value,
  priceRange: selectedPriceRange.value,
  stock: selectedStock.value,
}));

const facetCategory = computed(() =>
  filterCategories.value.find((category) => category.id === categoryId)
);
const subCategories = computed(
  () => facetCategory.value?.subcategories ?? data.value?.data.subcategories ?? []
);
const totalPages = computed(() => meta.value.last_page);
const isLoading = computed(
  () => categoryPending.value || (!catalogInitialized.value && !categoryNotFound.value)
);
const activeFilterCount = computed(
  () => Number(Boolean(selectedPriceRange.value)) + Number(Boolean(selectedStock.value))
);

const syncQuery = () => {
  router.replace({
    query: {
      ...(searchInput.value ? { search: searchInput.value } : {}),
      ...(selectedSub.value ? { subcategory: selectedSub.value } : {}),
      ...(selectedPriceRange.value ? { min_price: selectedPriceRange.value[0] } : {}),
      ...(selectedPriceRange.value ? { max_price: selectedPriceRange.value[1] } : {}),
      ...(selectedStock.value ? { stock: selectedStock.value } : {}),
      ...(selectedSort.value ? { sort: selectedSort.value } : {}),
      ...(currentPage.value > 1 ? { page: currentPage.value } : {}),
    },
  });
};

const getFilterQuery = () => ({
  search: searchInput.value,
  filters: activeFilters(),
});

const loadProducts = async () => {
  await fetchProducts({
    ...getFilterQuery(),
    page: currentPage.value,
    sort: selectedSort.value,
  });
};

const loadFilters = async () => {
  await fetchFilters(getFilterQuery());
};

const refreshProducts = async () => {
  syncQuery();
  await loadProducts();
};

const refreshCatalog = async () => {
  syncQuery();
  await Promise.all([loadProducts(), loadFilters()]);
};

const applyFilters = (filters: StoreProductFilters) => {
  selectedPriceRange.value = filters.priceRange ?? null;
  selectedStock.value = filters.stock ?? null;
  currentPage.value = 1;
  refreshCatalog();
};

const resetFilters = () => {
  const subcategoryChanged = selectedSub.value !== null;
  selectedSub.value = null;
  selectedPriceRange.value = null;
  selectedStock.value = null;
  currentPage.value = 1;
  if (!subcategoryChanged) refreshCatalog();
};

watch(searchInput, () => {
  currentPage.value = 1;
  refreshCatalog();
});

watch(selectedSub, () => {
  currentPage.value = 1;
  refreshCatalog();
});

watch(selectedSort, () => {
  currentPage.value = 1;
  refreshProducts();
});

const changePage = (page: number) => {
  currentPage.value = page;
  refreshProducts();
};

onMounted(() => {
  nextTick(async () => {
    try {
      if (!categoryNotFound.value) {
        await Promise.all([loadProducts(), loadFilters()]);
      }
    } finally {
      catalogInitialized.value = true;
    }
  });
});
</script>

<template>
  <v-container fluid class="browse-max-screen-size d-flex flex-column h-100">
    <BrowseHeader back-to="/">
      <template #title>
        <AppTypography type="title-3">{{ data?.data.label }}</AppTypography>
      </template>
    </BrowseHeader>

    <StoreProductFiltersDrawer
      v-model="isFilterDrawerOpen"
      :categories="filterCategories"
      :filters="drawerFilters"
      :price-bounds="priceRange"
      :stock-counts="stockCounts"
      :show-categories="false"
      :is-mobile="isMobile"
      @apply="applyFilters"
      @reset="resetFilters"
    />

    <template v-if="!categoryNotFound">
      <div class="category-toolbar d-flex align-center ga-3 mt-4">
        <AppSearchField
          v-model="searchInput"
          placeholder="Search products"
          :debounce="300"
          class="category-search"
        />

        <v-btn variant="outlined" rounded class="category-filter-btn" @click="isFilterDrawerOpen = true">
          <v-icon start size="20">mdi-filter-variant</v-icon>
          Filters
          <v-badge v-if="activeFilterCount" :content="activeFilterCount" inline color="primary" />
        </v-btn>

        <v-select
          v-model="selectedSort"
          :items="STORE_PRODUCT_SORT_OPTIONS"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          class="category-sort"
        />
      </div>

      <SubCategories
        v-if="subCategories.length > 0"
        v-model="selectedSub"
        :categories="subCategories"
        class="mt-4"
      />

      <AppTypography type="body-2" color="-secondary" class="mt-3">
        {{ facetTotal }} {{ facetTotal === 1 ? 'product' : 'products' }}
      </AppTypography>

      <div v-if="isLoading" class="category__loading">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <template v-else>
        <MarketplaceProductList v-if="products.length > 0" :products="products" />

        <div v-else-if="!productsPending" class="category__empty">
          <v-icon size="40" color="primary" icon="mdi-magnify-close" />
          <AppTypography type="title-3">No products match these filters</AppTypography>
        </div>
      </template>
    </template>

    <div v-if="!productsPending && totalPages > 1" class="category__pagination">
      <v-pagination
        :model-value="currentPage"
        :length="totalPages"
        :total-visible="5"
        rounded="circle"
        @update:model-value="changePage"
      />
    </div>

    <div v-if="categoryNotFound" class="category__unavailable">
      <div class="question-mark">
        <v-icon size="40" color="primary" icon="mdi-help-circle-outline"></v-icon>
      </div>
      <AppTypography type="title-1">Sorry, this category is no longer available</AppTypography>
    </div>
  </v-container>
</template>

<style lang="scss" scoped>
@media (width > 768px) {
  .browse-max-screen-size {
    padding-top: 10px !important;
  }
}

.category__unavailable {
  padding-inline: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;
  text-align: center;

  .question-mark {
    padding: 10px;
    background-color: rgb(var(--v-theme-primary-2));
    border-radius: 12px;
  }
}

.category__pagination {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.category-toolbar {
  .category-search {
    flex: 1 1 320px;
    max-width: 420px;
  }

  .category-filter-btn {
    border-color: rgb(var(--v-theme-stroke));
  }

  .category-sort {
    max-width: 220px;
    margin-left: auto;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;

    .category-search {
      flex-basis: 100%;
      max-width: none;
    }

    .category-sort {
      max-width: 180px;
    }
  }
}

.category__loading,
.category__empty {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.category__empty {
  flex-direction: column;
  gap: 16px;
  text-align: center;
}
</style>
